import Router from './routes/router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import jwtDecode from 'jwt-decode';
import { useDispatch, useSelector } from 'react-redux';
import { useLoginMutation } from './store/userApi';
import {
  useGoogleSignInMutation,
  useRefreshSessionMutation,
  useRevokeSessionMutation,
} from './store/authApi';
import {
  selectUser,
  setUser,
  setAccessToken,
  logoutUser,
  selectAccessToken,
} from './store/userSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import { socketLogin } from 'src/store/socket';
import {
  clearStoredSession,
  loadStoredSession,
  saveStoredSession,
  StoredSession,
} from './auth/session';
import LoadingPage from './routes/loadingPage';
import LoginPage from './routes/loginPage';

type AuthStatus = 'boot' | 'signedOut' | 'denied' | 'ready';

/**
 * In-house auth (replaces Auth0): a Google ID token from the login page is
 * exchanged at POST /auth/google for our own token pair; the access token
 * lives 1h and is renewed ahead of expiry via POST /auth/refresh, which also
 * rotates the stored refresh token.
 */
function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector(selectUser);
  const currentAccessToken = useSelector(selectAccessToken);
  const [login] = useLoginMutation();
  const [googleSignIn] = useGoogleSignInMutation();
  const [refreshSession] = useRefreshSessionMutation();
  const [revokeSession] = useRevokeSessionMutation();

  const [status, setStatus] = useState<AuthStatus>('boot');
  const timeoutHandle = useRef<NodeJS.Timeout | null>(null);
  const inFlight = useRef(false);

  const scheduleRefresh = useCallback((accessToken: string) => {
    if (timeoutHandle.current) {
      clearTimeout(timeoutHandle.current);
    }
    try {
      const { exp } = jwtDecode<{ exp: number }>(accessToken);
      // A minute early, so requests never race the expiry.
      const delay = Math.max(exp * 1000 - Date.now() - 60_000, 10_000);
      timeoutHandle.current = setTimeout(() => renewSession(), delay);
    } catch (e) {
      console.error('scheduleRefresh', e);
    }
    // renewSession is hoisted below; both are stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Shared tail of sign-in and refresh: persist, wire up, gate on admin. */
  const completeSignIn = useCallback(
    async (pair: StoredSession) => {
      saveStoredSession(pair);
      dispatch(setAccessToken(pair.accessToken));
      const me = await login().unwrap();
      dispatch(setUser(me));
      if (!me.adminRole) {
        // Signed in fine, but this is the admin dashboard: without a role
        // every endpoint would 403. Drop the session and say so.
        clearStoredSession();
        dispatch(logoutUser());
        setStatus('denied');
        return;
      }
      socketLogin(pair.accessToken);
      scheduleRefresh(pair.accessToken);
      setStatus('ready');
      if (location.pathname === '/logout') {
        navigate('/');
      }
    },
    [dispatch, login, navigate, location.pathname, scheduleRefresh]
  );

  const renewSession = useCallback(async () => {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    try {
      const stored = loadStoredSession();
      if (!stored?.refreshToken) {
        setStatus('signedOut');
        return;
      }
      try {
        const envelope = await refreshSession({
          refreshToken: stored.refreshToken,
        }).unwrap();
        await completeSignIn({
          accessToken: envelope.accessToken,
          refreshToken: envelope.refreshToken,
        });
      } catch (e) {
        const httpStatus = (e as { status?: number })?.status;
        if (httpStatus === 401 || httpStatus === 403) {
          // The server rejected the refresh token — the session is dead.
          clearStoredSession();
          dispatch(logoutUser());
          setStatus('signedOut');
        } else {
          // Transient (offline, 5xx): keep the session and try again soon.
          console.error('Session refresh failed, retrying', e);
          if (timeoutHandle.current) {
            clearTimeout(timeoutHandle.current);
          }
          timeoutHandle.current = setTimeout(() => renewSession(), 30_000);
        }
      }
    } finally {
      inFlight.current = false;
    }
  }, [completeSignIn, dispatch, refreshSession]);

  // Cold start: restore the persisted session, if any.
  useEffect(() => {
    renewSession();
    // Once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refocusing a long-idle tab: renew if less than 10 minutes remain.
  const windowOnFocus = useCallback(() => {
    if (status !== 'ready') {
      return;
    }
    let shouldRefresh = true;
    if (currentAccessToken) {
      try {
        const { exp } = jwtDecode<{ exp: number }>(currentAccessToken);
        if (exp && exp * 1000 > Date.now() + 600_000) {
          shouldRefresh = false;
        }
      } catch (e) {
        console.error(e);
      }
    }
    if (shouldRefresh) {
      renewSession();
    }
  }, [renewSession, currentAccessToken, status]);

  useEffect(() => {
    window.addEventListener('focus', windowOnFocus);
    return () => {
      window.removeEventListener('focus', windowOnFocus);
    };
  }, [windowOnFocus]);

  // The /logout route: revoke the refresh token server-side, then sign out.
  useEffect(() => {
    if (location.pathname === '/logout') {
      const stored = loadStoredSession();
      if (stored?.refreshToken) {
        revokeSession({ refreshToken: stored.refreshToken });
      }
      if (timeoutHandle.current) {
        clearTimeout(timeoutHandle.current);
      }
      clearStoredSession();
      dispatch(logoutUser());
      setStatus('signedOut');
    }
  }, [location.pathname, dispatch, revokeSession]);

  const onCredential = useCallback(
    async (idToken: string) => {
      const envelope = await googleSignIn({ idToken }).unwrap();
      await completeSignIn({
        accessToken: envelope.accessToken,
        refreshToken: envelope.refreshToken,
      });
    },
    [googleSignIn, completeSignIn]
  );

  if (status === 'ready' && currentUser?.id?.length) {
    return <Router />;
  }
  if (status === 'signedOut' || status === 'denied') {
    return (
      <LoginPage
        onCredential={onCredential}
        accessDenied={status === 'denied'}
      />
    );
  }
  return <LoadingPage />;
}
export default App;
